import React, { Fragment, useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  InputAdornment,
  Paper,
  IconButton,
  Dialog,
  tableCellClasses,
} from "@mui/material";
import ClientServices from "services/Client";
import VehicleBookingServices from "services/VehicleBooking";
import InputField from "components/Input";
import DatePicker from "components/DatePicker";
import { PrimaryButton } from "components/Buttons";
import Colors from "assets/Style/Colors";
import { SearchIcon, FontFamily } from "assets";
import SelectField from "components/Select";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import { CancelOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import VendorServices from "services/Vendor";
import UserServices from "services/User";
import SystemServices from "services/System";
import { createRoot } from "react-dom/client";
import DamageInvoiceErp from "components/erp_invoices/damages_invoice";
import { useAuth } from "context/UseContext";

const Row = styled(TableRow)({ border: 0 });
const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    textAlign: "center",
    whiteSpace: "nowrap",
    background: Colors.primary,
    color: Colors.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    textWrap: "nowrap",
    padding: "5px !important",
    ".MuiBox-root": {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "24px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      textWrap: "nowrap",
    },
    ".MuiButtonBase-root": {
      padding: "8px",
      width: "28px",
      height: "28px",
    },
  },
}));

const useStyles = makeStyles({
  loaderWrap: {
    display: "flex",
    height: 100,
  },
});

const CreateDamages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectNotesDate, setSelectNotesDate] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(false);
  const {
    register,
    formState: { errors },
    getValues: getValues1,
  } = useForm();
  const [isSearching, setIsSearching] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState(
    location.state?.selectedRows || []
  );
  const [filters, setFilters] = useState({});
  const [pageLimit, setPageLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [bookingCustomers, setBookingCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedBookingCustomer, setSelectedBookingCustomer] = useState(null);

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const getVendorDropdown = async () => {
    try {
      const { data } = await VendorServices.getVendorDropdown();
      setVendors(data?.vendors);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getUsers = async (page, limit, search) => {
    try {
      const params = { page: 1, limit: 100, search };
      const { data } = await UserServices.getUsers(params);
      const rows = data?.users?.rows ?? [];

      const staffOnly = rows.filter((user) => user?.user_type_id === "S");

      const staffData = staffOnly.map((user) => ({
        id: user?.id,
        name: user?.name,
      }));

      setStaff(staffData);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getAllSLPVehicles = async (page, limit, filter) => {
    try {
      const Page = page ?? currentPage;
      const Limit = limit ?? pageLimit;
      const Filter = { ...filters, ...filter };

      const params = { page: Page, limit: Limit, ...Filter };
      const { data } = await ClientServices.getSLPVehicles(params);

      if (filter?.search) {
        const matchedVehicle = data.vehicles.find(
          (v) => v.vin === filter.search
        );

        if (!matchedVehicle) {
          ErrorToaster("VIN not found.");
          return;
        }

        if (matchedVehicle?.damage !== null) {
          ErrorToaster(
            "This VIN already has an existing damage record and damage paid, cannot be added again."
          );
          return;
        }

        if (rows.length >= 1) {
          ErrorToaster(
            "Only one VIN can be added. Remove the existing VIN to add another."
          );
          return;
        }

        if (!rows.some((r) => r.vin === matchedVehicle.vin)) {
          if (!matchedVehicle?.shipping_vendor) {
            ErrorToaster(
              "Shipping vendor for this vin is not found, please update shipping vendor."
            );
            return;
          }
          const newRow = {
            id: matchedVehicle.id,
            shipping_vendor: matchedVehicle?.shipping_vendor,
            customer: matchedVehicle.customer?.name,
            customer_id: matchedVehicle?.customer?.id,
            buyer_id: matchedVehicle.buyer_id,
            buyer_name: matchedVehicle.buyer?.name,
            vin: matchedVehicle.vin,
            lot: matchedVehicle.lot_number,
            make:
              matchedVehicle.veh_make?.name || matchedVehicle?.make_name || "",
            model:
              matchedVehicle.veh_model?.name ||
              matchedVehicle?.model_name ||
              "",
            color: matchedVehicle.color || "",
            discount: 0,
            damage_reason: "",
          };
          setRows([newRow]);
        } else {
          ErrorToaster("This VIN is already added.");
        }
      } else {
        setVehicles(data.vehicles);
      }

      setCurrentPage(Page);
      setPageLimit(Limit);
      setFilters(Filter);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getBookingCustomers();
    getAllSLPVehicles();
    getVendorDropdown();
    getUsers();
  }, []);

  useEffect(() => {
    if (vendors?.length > 0) {
      const findVendor = vendors?.find(
        (val) => val?.id === rows?.[0]?.shipping_vendor
      );
      setSelectedVendor({ id: findVendor?.id, name: findVendor?.name });
    }
  }, [vendors, rows]);

  const handleEditClick = () => {
    if (!rows.length) return;
    const rowToEdit = rows[0];
    setSelectedRows(rowToEdit);
    setOpenEditModal(true);
    setNotes((prev) => ({ ...prev, [rowToEdit.id]: rowToEdit.notes || "" }));
  };

  const handleDiscountSChange = (id, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, discount: value } : row))
    );
  };

  const handleDamageReasonChange = (id, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, damage_reason: value } : row
      )
    );
  };

  const handleNotesChange = (e) => {
    const { value } = e.target;
    setNotes((prev) => ({
      ...prev,
      [selectedRows.id]: value,
    }));
  };

  const handleSaveNotes = () => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === selectedRows.id
          ? { ...row, notes: notes[selectedRows.id] }
          : row
      )
    );
    setOpenEditModal(false);
  };

  const handleDelete = (vin) => {
    const updatedRows = rows.filter((row) => row.vin !== vin);
    setRows(updatedRows);
  };

  const getBookingCustomers = async (search) => {
    try {
      const params = { page: 1, limit: 50, search };
      const { data } = await VehicleBookingServices.getBookingCustomers(params);
      setBookingCustomers(
        data?.filters.map((item) => ({
          id: item?.customer_id,
          name: item?.customer_name,
        }))
      );
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const handleFilter = async (data) => {
    if (!data || isSearching) return;
    setIsSearching(true);
    try {
      await getAllSLPVehicles(1, undefined, data);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setIsSearching(false);
    }
  };



  const handleSubmitDamages = async () => {
    try {
      if (rows.length !== 1) {
        ErrorToaster("Please add exactly one VIN before submitting.");
        return;
      }

      const row = rows[0];

      if (!selectedVendor?.id) {
        ErrorToaster("Please select the 'Vendor Damaged By'.");
        return;
      }

      if (!selectedBookingCustomer?.id) {
        ErrorToaster("Please select the Approved By.");
        return;
      }

      const creditAmount = Number(row.discount || 0);
      if (!(creditAmount > 0)) {
        ErrorToaster("Please enter a valid credited amount > 0.");
        return;
      }

      if (!row.damage_reason || !row.damage_reason.trim()) {
        ErrorToaster("Please enter a Damage Reason.");
        return;
      }

      const booking_id = row?.vehicle?.booking_id ?? row.id;

      if (!booking_id) {
        ErrorToaster("Missing booking_id for this VIN.");
        return;
      }

      const payload = {
        booking_id,
        vendor_id: selectedVendor?.id,
        credit_amount: creditAmount,
        reason: row?.damage_reason.trim(),
        customer_id: row?.customer_id,
        damage_type: "general",
        is_active: true,
        booked_date: selectedDate || new Date(),
        approved_by: selectedBookingCustomer.id,
        vin: row.vin,
        lot_number: row.lot,
        vehicle_make: row.make,
        vehicle_model: row.model,
        color: row.color,
        notes: notes[row.id] ?? row.notes ?? "",
        notes_date: selectNotesDate || new Date(),
      };

      setLoading(true);
      const result = await VendorServices.createDamages(payload);


      if (result?.status === true && result?.responseCode === 200) {
        const damage_id = result?.data?.damage_id;

        const { data } = await VendorServices.getDamagesDetail({ damage_id: damage_id });

        if (!data) {
          console.log("No record found to upload");
          return;
        }

        const uploadFiles = [];

        const file = await new Promise((resolve, reject) => {
          try {
            const container = document.createElement("div");
            container.style.position = "absolute";
            container.style.top = "-99999px";
            container.style.left = "-99999px";
            container.style.width = "1200px";
            document.body.appendChild(container);

            const root = createRoot(container);

            const handleDone = (pdfBlob) => {
              setTimeout(() => {
                try {
                  root.unmount();
                  container.remove();
                } catch (err) {
                  console.error("❌ Cleanup Error", err);
                }
                resolve(pdfBlob);
              }, 200);
            };

            root.render(
              <DamageInvoiceErp
                damagesData={data?.damage}
                onDone={handleDone}
                user={user}
              />
            );
          } catch (err) {
            console.error("❌ PDF Generation Error", err);
            reject(err);
          }
        });

        const pdfFile =
          file instanceof Blob
            ? new File([file], `damage-${data?.damage?.booking?.vin}.pdf`, {
              type: "application/pdf",
            })
            : null;

        uploadFiles.push({
          damages_id: damage_id,
          file: pdfFile,
        });

        const formData = new FormData();
        formData.append("invoice_type", "damages");

        uploadFiles.forEach((file, index) => {
          formData.append(`damages_data[${index}][damages_id]`, damage_id);
          formData.append(`damages_data[${index}][file]`, file.file);
        });

        await SystemServices.uploadERPInvoices(formData);

        setLoading(false);
        SuccessToaster("Submitted successfully.");
        navigate("/damages-list");
      }

    } catch (e) {
      ErrorToaster(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        sx={{
          "& .MuiDialog-paper": {
            width: "30%",
            height: "auto",
            borderRadius: 2,
            py: { xs: 2, md: 4 },
            px: { xs: 3, md: 6 },
          },
        }}
      >
        <IconButton
          onClick={() => setOpenEditModal(false)}
          sx={{ position: "absolute", right: 13, top: 13 }}
        >
          <CancelOutlined />
        </IconButton>
        <Box component={"form"}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
            Notes
          </Typography>
          <DatePicker
            size={"small"}
            label={"Date"}
            value={
              selectNotesDate ? moment(selectNotesDate).toDate() : new Date()
            }
            onChange={(date) =>
              setSelectNotesDate(moment(date).format("YYYY-MM-DD"))
            }
            disabled={true}
          />

          <InputField
            fullWidth
            multiline
            rows={3}
            value={notes[selectedRows?.id] ?? ""}
            onChange={handleNotesChange}
            placeholder="Enter notes"
          />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <PrimaryButton title="Submit" onClick={handleSaveNotes} />
          </Box>
        </Box>
      </Dialog>

      <Grid
        container
        spacing={1}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Grid item xs={12} sm={12} md={11}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              mr: 4,
              my: 4,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: Colors.charcoalGrey,
                fontFamily: FontFamily.NunitoRegular,
              }}
            >
              Create Damages
            </Typography>
          </Box>
        </Grid>

        {/* Filters */}
        <Grid item md={11}>
          <Box sx={{ m: "10px 0 10px 0" }}>
            <Grid
              container
              spacing={2}
              alignItems={"center"}
              component={"form"}
            >
              <Grid item xs={12} sm={2.5} sx={{ marginTop: "5px" }}>
                <InputField
                  size={"small"}
                  inputStyle={{ backgroundColor: "#f5f5f5" }}
                  label={"Search"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  placeholder={"Search VIN"}
                  register={register("search")}
                />
              </Grid>

              <Grid item xs={12} sm={2.5}>
                <DatePicker
                  size={"small"}
                  label={"Select Date"}
                  value={
                    selectedDate ? moment(selectedDate).toDate() : new Date()
                  }
                  onChange={(date) =>
                    setSelectedDate(moment(date).format("YYYY-MM-DD"))
                  }
                />
              </Grid>

              <Grid item xs={12} sm={2.5} sx={{ marginTop: "7px" }}>
                <SelectField
                  size={"small"}
                  label={"Vendor Damaged By"}
                  options={vendors}
                  selected={selectedVendor}
                  onSelect={(value) => setSelectedVendor(value)}
                  error={errors?.vendor?.message}
                  register={register("vendor")}
                />
              </Grid>

              <Grid item xs={12} sm={2.5}>
                <SelectField
                  size={"small"}
                  onSearch={(v) => getUsers(v)}
                  label={"Approved By"}
                  options={staff}
                  selected={selectedBookingCustomer}
                  onSelect={(value) => {
                    setSelectedBookingCustomer(value);
                  }}
                  register={register("approvedBy")}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Box sx={{ mt: "16px" }}>
                  <PrimaryButton
                    onClick={() => {
                      const searchVal = getValues1("search");
                      if (searchVal) handleFilter({ search: searchVal });
                    }}
                    title="Search"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                borderRadius: 2,
                maxHeight: "calc(100vh - 330px)",
                mt: 2,
              }}
              className="table-box"
            >
              <Table stickyHeader sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <Cell>Customer Name</Cell>
                    <Cell>VIN</Cell>
                    <Cell>LOT</Cell>
                    <Cell>Model/Make/Color</Cell>
                    {/* <Cell>Approved By</Cell> */}
                    <Cell>Credited Amt $</Cell>
                    <Cell>Damage Reason</Cell>
                    <Cell>Notes</Cell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          bgcolor:
                            rows.indexOf(row) % 2 !== 0 ? "#EFF8E7" : "inherit",
                        }}
                      >
                        <Cell>{row.customer}</Cell>
                        <Cell>{row.vin}</Cell>
                        <Cell>{row.lot}</Cell>
                        <Cell>
                          {row?.model}
                          {"-"}
                          {row.make}
                          {"-"}
                          {row.color}
                        </Cell>
                        {/* <Cell>{"-"}</Cell> */}
                        <Cell sx={{ width: "10%" }}>
                          <InputField
                            size="small"
                            type="number"
                            value={row.discount}
                            onInput={(e) =>
                              handleDiscountSChange(
                                row.id,
                                parseFloat(e.target.value || 0)
                              )
                            }
                          />
                        </Cell>
                        {/* NEW: Damage Reason input */}
                        <Cell sx={{ width: "22%" }}>
                          <InputField
                            size="small"
                            placeholder="Enter damage reason"
                            value={row.damage_reason}
                            onInput={(e) =>
                              handleDamageReasonChange(row.id, e.target.value)
                            }
                          />
                        </Cell>
                        <Cell>
                          <Box
                            component={"div"}
                            onClick={() => handleEditClick(row)}
                          >
                            <EditIcon />
                          </Box>
                        </Cell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <Cell
                        colSpan={11}
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          fontFamily: FontFamily.NunitoRegular,
                          fontSize: "16px",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          No Data Found
                        </Box>
                      </Cell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>

        {/* Submit Button */}
        <Grid item md={11} justifyContent="end">
          <Box
            sx={{
              mt: "28px",
              mb: "28px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <PrimaryButton
              onClick={handleSubmitDamages}
              title="Submit"
              loading={loading}
            />
          </Box>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default CreateDamages;
